import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STEPS = [
  {
    num: '01',
    icon: '🎯',
    title: 'Discovery',
    subtitle: 'Entender el problema',
    desc: 'Antes de escribir una sola línea de código, entiendo el negocio. Hablo con el cliente, defino los objetivos, identifico los riesgos y establezco métricas de éxito claras.',
    details: ['Reunión de kickoff', 'Análisis de requerimientos', 'Definición de alcance', 'Estimación de tiempos'],
    color: '#ff4500',
    duration: '1-2 días',
  },
  {
    num: '02',
    icon: '🧠',
    title: 'Arquitectura',
    subtitle: 'Diseñar la solución',
    desc: 'Defino la arquitectura técnica: qué stack usar, cómo estructura la base de datos, qué APIs necesito, cómo se conectan las partes. Nada se improvisa.',
    details: ['Diseño del sistema', 'Modelado de datos', 'Selección de tecnologías', 'Documentación técnica'],
    color: '#b794f4',
    duration: '1-3 días',
  },
  {
    num: '03',
    icon: '⚡',
    title: 'Desarrollo',
    subtitle: 'Construir con calidad',
    desc: 'Desarrollo iterativo con entregas frecuentes. El cliente ve progreso real desde la primera semana, no espera meses para ver algo funcionar.',
    details: ['Sprints semanales', 'Demos frecuentes', 'Código limpio y documentado', 'Testing continuo'],
    color: '#68d391',
    duration: 'Variable',
  },
  {
    num: '04',
    icon: '🔍',
    title: 'Testing',
    subtitle: 'Garantizar la calidad',
    desc: 'Pruebas exhaustivas antes de cualquier deploy. Tests de funcionamiento, compatibilidad, carga y seguridad. Lo que llega a producción, funciona.',
    details: ['Tests funcionales', 'Compatibilidad cross-browser', 'Performance testing', 'Revisión de seguridad'],
    color: '#f6ad55',
    duration: '2-4 días',
  },
  {
    num: '05',
    icon: '🚀',
    title: 'Deploy',
    subtitle: 'A producción sin drama',
    desc: 'Deploy en la nube con SSL, dominio, CI/CD y monitoreo. Tu app llega al mundo de forma segura, rápida y escalable desde el día uno.',
    details: ['Configuración de servidor', 'SSL y dominio', 'CI/CD pipeline', 'Monitoreo y alertas'],
    color: '#63b3ed',
    duration: '1-2 días',
  },
  {
    num: '06',
    icon: '🔄',
    title: 'Soporte',
    subtitle: 'Después del lanzamiento',
    desc: 'No desaparezco después del deploy. Ofrezco soporte post-lanzamiento, monitoreo, corrección de bugs y mejoras iterativas según el feedback real.',
    details: ['Corrección de bugs', 'Mejoras iterativas', 'Capacitación al equipo', 'Documentación final'],
    color: '#fc8181',
    duration: 'Continuo',
  },
];

// Animated counter
const Counter = ({ value }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const observed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !observed.current) {
        observed.current = true;
        let start = 0;
        const end = parseInt(value);
        if (isNaN(end)) return;
        const dur = 1200;
        const step = dur / end;
        const timer = setInterval(() => {
          start += 1;
          setCount(start);
          if (start >= end) clearInterval(timer);
        }, step);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);

  const raw = parseInt(value);
  if (isNaN(raw)) return <span ref={ref}>{value}</span>;
  return <span ref={ref}>{count}{value.replace(String(raw), '')}</span>;
};

const METRICS = [
  { value: '3+', label: 'Años de experiencia', icon: '📅' },
  { value: '15+', label: 'Proyectos entregados', icon: '✅' },
  { value: '100%', label: 'Clientes satisfechos', icon: '⭐' },
  { value: '24h', label: 'Tiempo de respuesta', icon: '⚡' },
];

const WorkProcess = () => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section id="process" style={{ background: '#0d0d0d', padding: '6rem 0', overflow: 'hidden' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 1.5rem' }}>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '5rem' }}
        >
          <p style={{ color: '#ff4500', fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
            Metodología
          </p>
          <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', color: '#f2f2f2', fontWeight: 800, marginBottom: '1rem' }}>
            ¿Cómo trabajo?
            <span style={{ color: '#ff4500' }}> Sin sorpresas.</span>
          </h2>
          <p style={{ color: 'rgba(242,242,242,0.45)', maxWidth: '540px', margin: '0 auto', lineHeight: 1.8, fontSize: '0.93rem' }}>
            Un proceso claro, predecible y con comunicación constante. Sabés en todo momento en qué etapa está tu proyecto.
          </p>
        </motion.div>

        {/* Metrics row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '5rem' }}
        >
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              style={{
                background: 'rgba(255,69,0,0.04)', border: '1px solid rgba(255,69,0,0.12)',
                borderRadius: '16px', padding: '1.5rem', textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>{m.icon}</div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: '#ff4500', lineHeight: 1 }}>
                <Counter value={m.value} />
              </div>
              <div style={{ color: 'rgba(242,242,242,0.4)', fontSize: '0.78rem', marginTop: '0.4rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                {m.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Steps interactive */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', alignItems: 'start' }}>

          {/* Step list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {STEPS.map((step, i) => (
              <motion.button
                key={step.num}
                onClick={() => setActiveStep(i)}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  padding: '0.85rem 1.1rem', borderRadius: '12px',
                  background: activeStep === i ? `${step.color}12` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${activeStep === i ? step.color + '40' : 'rgba(255,255,255,0.06)'}`,
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.25s',
                  width: '100%',
                }}
              >
                <span style={{
                  fontSize: '1.1rem', width: '36px', height: '36px',
                  borderRadius: '10px', background: activeStep === i ? `${step.color}20` : 'rgba(255,255,255,0.04)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {step.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <span style={{ color: activeStep === i ? '#f2f2f2' : 'rgba(242,242,242,0.55)', fontWeight: 700, fontSize: '0.9rem' }}>
                      {step.title}
                    </span>
                    <span style={{ color: 'rgba(242,242,242,0.25)', fontSize: '0.7rem', flexShrink: 0 }}>{step.duration}</span>
                  </div>
                  <div style={{ color: 'rgba(242,242,242,0.35)', fontSize: '0.75rem' }}>{step.subtitle}</div>
                </div>
                <span style={{ color: activeStep === i ? step.color : 'rgba(242,242,242,0.15)', fontSize: '0.8rem' }}>
                  {activeStep === i ? '→' : '›'}
                </span>
              </motion.button>
            ))}
          </div>

          {/* Detail panel */}
          <div style={{ position: 'sticky', top: '100px' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3 }}
                style={{
                  background: `linear-gradient(135deg, ${STEPS[activeStep].color}0a 0%, rgba(255,255,255,0.01) 100%)`,
                  border: `1px solid ${STEPS[activeStep].color}25`,
                  borderRadius: '20px', padding: '2rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
                  <span style={{
                    width: '50px', height: '50px', borderRadius: '14px',
                    background: `${STEPS[activeStep].color}18`,
                    border: `1px solid ${STEPS[activeStep].color}35`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem',
                  }}>
                    {STEPS[activeStep].icon}
                  </span>
                  <div>
                    <div style={{ color: STEPS[activeStep].color, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                      Paso {STEPS[activeStep].num}
                    </div>
                    <h3 style={{ color: '#f2f2f2', fontWeight: 800, fontSize: '1.2rem', lineHeight: 1.2 }}>
                      {STEPS[activeStep].title}
                    </h3>
                  </div>
                </div>

                <p style={{ color: 'rgba(242,242,242,0.6)', lineHeight: 1.8, fontSize: '0.88rem', marginBottom: '1.5rem' }}>
                  {STEPS[activeStep].desc}
                </p>

                <div>
                  <p style={{ color: 'rgba(242,242,242,0.3)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.75rem' }}>
                    Qué incluye
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {STEPS[activeStep].details.map((d) => (
                      <div key={d} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: STEPS[activeStep].color, flexShrink: 0 }} />
                        <span style={{ color: 'rgba(242,242,242,0.55)', fontSize: '0.83rem' }}>{d}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.9rem', borderRadius: '20px', background: `${STEPS[activeStep].color}12`, border: `1px solid ${STEPS[activeStep].color}25` }}>
                  <span style={{ color: STEPS[activeStep].color, fontSize: '0.75rem', fontWeight: 600 }}>⏱ Duración estimada: {STEPS[activeStep].duration}</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginTop: '5rem' }}
        >
          <p style={{ color: 'rgba(242,242,242,0.35)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
            ¿Listo para arrancar?
          </p>
          <motion.a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            whileHover={{ scale: 1.04, boxShadow: '0 0 35px rgba(255,69,0,0.5)' }}
            whileTap={{ scale: 0.97 }}
            style={{
              display: 'inline-block', padding: '0.9rem 2.5rem',
              background: 'linear-gradient(135deg, #ff4500, #ff7b47)',
              color: 'white', borderRadius: '10px', textDecoration: 'none',
              fontWeight: 700, fontSize: '0.95rem',
              boxShadow: '0 0 20px rgba(255,69,0,0.3)',
            }}
          >
            Hablemos de tu proyecto →
          </motion.a>
        </motion.div>

      </div>
    </section>
  );
};

export default WorkProcess;
